import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useContext } from "react";

import { brandAssets } from "../../../assets/logoRegistry";
import { siteMetadata } from "../../../content/siteMetadata";
import { ApplicationContext } from "../../../context/GlobalState";
import useMobileMenu from "../../../hooks/useMobileMenu";
import MetaText from "../MetaText";

/**
 * Top navigation bar for the marketing site.
 * Translation: Top navigation bar for the marketing site.
 *
 * @param {Object} props - Navigation props.
 * @returns {JSX.Element} Navigation element.
 */
const Navigation = ({ isSticky }) => {
  const location = useLocation();
  const [state] = useContext(ApplicationContext);
  const { isMobileMenuOpen, toggleMenu } = useMobileMenu();

  /**
   * Determine whether a navigation link is active.
   * Translation: Determine whether a navigation link is active.
   *
   * @param {Object} link - Navigation link config.
   * @returns {boolean} Active state.
   */
  const isActiveLink = (link) => {
    if (link.id === "blog") {
      return location.pathname.startsWith("/blog");
    }

    if (link.id === "kanzi") {
      return location.pathname === "/" && !state.activeSection;
    }

    if (link.id === "how-it-works") {
      return location.pathname === "/how-it-works";
    }

    if (location.pathname !== "/") {
      return false;
    }

    return state.activeSection === link.id;
  };

  return (
    <nav
      className={`site-nav ${isSticky ? "navbar-sticky" : ""}`}
      aria-label="Main navigation"
    >
      <div className="site-nav__inner">
        <Link
          to="/"
          className="site-nav__brand"
          aria-label="Data Chimp Labs homepage"
          title="Data Chimp Labs | Forward-Deployed Data & AI Engineering"
        >
          <img
            src={brandAssets.logoWhite}
            className="site-nav__brand-mark"
            alt="Data Chimp Labs"
            title="Data Chimp Labs Logo"
            width="640"
            height="180"
          />
        </Link>

        <div className="site-nav__links">
          {siteMetadata.navigationLinks.map((link) => (
            <Link
              key={link.id}
              to={link.href}
              className={isActiveLink(link) ? "is-active" : ""}
              aria-current={isActiveLink(link) ? "true" : undefined}
              title={`Go to ${link.label}`}
            >
              <MetaText>{link.label}</MetaText>
            </Link>
          ))}
        </div>

        <Link
          to="/#contact"
          className="site-nav__cta"
          title="Schedule a consultation with our engineering team"
        >
          Talk to Us
        </Link>

        <button
          type="button"
          className="site-nav__menu-button"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          onClick={toggleMenu}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
